<template>
  <component
    :is="interactive ? 'button' : 'article'"
    class="sale-card"
    :class="{
      'sale-card--live': sale.live,
      'sale-card--selected': selected,
      'sale-card--openable': openable,
    }"
    :type="interactive ? 'button' : null"
    :aria-current="selected ? 'true' : null"
    @click="interactive ? $emit('select', sale) : null"
  >
    <!-- The stretched open control: a sibling laid over the whole card, not a
         wrapper around it, so the accessory above it can be a control of its
         own without nesting one button in another. Only on an <article>. -->
    <button
      v-if="openable"
      type="button"
      class="sale-card__open"
      :aria-label="openLabel"
      @click="$emit('open', sale)"
    />
    <div class="sale-card__head">
      <div class="sale-card__tile" :class="{ 'sale-card__tile--live': sale.live }">
        <!-- Decorative: the status line already says in words what the glyph
             says in a picture, so announcing it repeats the sale. -->
        <i class="pi" :class="glyphClass" aria-hidden="true" />
      </div>

      <div class="sale-card__info">
        <div class="sale-card__name">{{ sale.title }}</div>
        <!-- Optional line straight under the name, beside the tile rather than
             below it, so a card that has one (My Auctions' location) is a line
             shorter than one that puts it under the status. -->
        <div v-if="sale.subtitle" class="sale-card__subtitle">{{ sale.subtitle }}</div>
        <!-- Chips never wrap their own text (a squeezed chip turns into an
             awkward tall two-line pill) — the ROW wraps instead, so a chip that
             doesn't fit drops whole onto the next line. -->
        <div v-if="showChipsRow" class="sale-card__chips">
          <span v-if="showsTypeChip" class="sale-card__type" :class="typeModifier">{{ sale.typeLabel }}</span>
          <span
            v-if="likedLabel"
            class="sale-card__liked"
          >
            <i class="pi pi-star-fill" aria-hidden="true" />{{ likedLabel }}
          </span>
          <slot name="chips" />
        </div>
      </div>

      <div v-if="openable && $slots.accessory" class="sale-card__accessory">
        <slot name="accessory" />
      </div>
      <slot v-else name="accessory" />
    </div>

    <div
      v-if="sale.status"
      class="sale-card__status"
      :class="{ 'sale-card__status--live': sale.live }"
    >{{ sale.status }}</div>
    <!-- Optional where/distance line (Find a sale / SoT 9b). Tertiary, below
         status; the card never invents it — the caller finishes the sentence. -->
    <div v-if="sale.where" class="sale-card__where">{{ sale.where }}</div>
  </component>
</template>

<script>
// The auction types this card can be handed, and the chip modifier each takes.
//
// THREE TYPES, TWO SPELLINGS EACH. The event runtime names a sale's type
// `simulcastAuction`, `timedAuction` or `automatedAuction`, and the client SDK
// rewrites each to a display label — Simulcast, Timed, Sequential — before any
// screen sees one. AUTOMATED is DISPLAYED as Sequential: one type spelled two
// ways rather than two types, which is why both spellings land on one modifier
// and a card fed from either end of the pipeline draws the same chip.
//
// SIMULCAST = red, AUTOMATED = blue, TIMED ONLINE = amber (handoff semantics).
//
// A plain `<script>` block rather than `<script setup>`: the map is the same for
// every card, so it is built once for the module instead of once per instance.
const TYPE_MODIFIERS = Object.freeze({
  simulcastAuction: 'sale-card__type--simulcast',
  Simulcast: 'sale-card__type--simulcast',
  timedAuction: 'sale-card__type--timed',
  Timed: 'sale-card__type--timed',
  automatedAuction: 'sale-card__type--automated',
  Sequential: 'sale-card__type--automated',
});

// What a type the map does not name draws as: the neutral chip, under a name
// that says the card did not recognize the value. It is deliberately not any
// auction type's own modifier — while it was, a sale whose type was missing
// rendered exactly like a correctly typed one and nothing on the card separated
// them.
const UNRECOGNIZED_TYPE_MODIFIER = 'sale-card__type--unrecognized';
</script>

<script setup>
import { computed, useSlots } from 'vue';

// One sale in a browse or switch list. My Sales and the live-bidding sale
// search each built this card independently — same markup, same CSS, and a
// byte-identical type-to-color helper — differing only in what sits at the end
// of the head row and which extra chip they showed.
//
// The trailing control is a slot rather than a prop because the two uses are
// genuinely different verbs: My Sales follows a sale, the search sheet selects
// one. Neither belongs to the card.
//
// SLOTS
//   accessory — trailing control in the head row (a follow toggle, a check).
//               It must NOT hold an interactive control while `interactive` is
//               set: the card is then a <button> and both slots render inside
//               it, so a control here nests one button in another and its press
//               also selects the sale. The whole card being the tap target is
//               the deliberate promise, so the constraint sits on the slot
//               rather than on the markup.
//               With `openLabel` set the card stays an <article>, draws its
//               own stretched open button under everything, and lifts the
//               accessory above it — so the accessory MAY be a control (My
//               Sales' follow toggle) and the rest of the card opens the sale.
//   chips     — extra chips after the type / liked chips (caller-owned).
//               Lies under the open button: chips are words, not controls.
defineOptions({ name: 'SaleCard' });

const props = defineProps({
  /**
   * The sale to draw, and the consumer's own record: the whole object comes
   * back out on `select`, so a screen may carry fields the card never reads.
   * Fields read here:
   *   title         the headline, e.g. 'Coastal Simulcast'
   *   subtitle      optional line right under the title, e.g. 'Auburn, WA · 602 mi'
   *   typeLabel     the caller's own words for the auction type, e.g. 'TIMED ONLINE'
   *   type          the auction type itself, which chooses the chip's color
   *   status        a pre-formatted, human-readable line ('Live now · 4 lanes'),
   *                 derived by the caller
   *   where         optional city · distance line under the status (Find a sale)
   *   live          swaps the tile glyph and takes the attention frame
   *   likedCount    optional ★ liked chip count (Find a sale / SoT 9b)
   *   likedLabel    optional pre-formatted liked chip text; wins over count
   *
   * The validator checks the two of those whose absence renders something
   * WRONG rather than something missing: an empty headline, and an empty
   * status-colored pill with padding and no words. `type` is deliberately
   * outside it — a value the map above does not name is a supported case with
   * a defined outcome — and `status` and `live` are optional by design.
   *
   * Both strings are TRIMMED before they are measured. A string of spaces
   * satisfies a length check and renders exactly the empty pill the check
   * exists to catch.
   */
  //
  // `typeLabel` may be ABSENT (a row with no auction type, such as My Auctions'
  // house rows, which draw with `showType` off) but never blank: a blank one is
  // the empty pill. A card that shows its type without one warns at setup.
  sale: {
    type: Object,
    required: true,
    validator: (value) => Boolean(value)
      && typeof value.title === 'string' && value.title.trim().length > 0
      && (value.typeLabel == null
        || (typeof value.typeLabel === 'string' && value.typeLabel.trim().length > 0)),
  },
  // Renders as a <button> and emits `select`. A browse list that only follows
  // sales leaves this off and stays a non-interactive <article>.
  interactive: { type: Boolean, default: false },
  // Currently-attended sale in a switcher, or a marked row in a browse list.
  // Conveyed as `aria-current` as well as a frame, because the frame is color
  // alone and two rows otherwise sound identical.
  selected: { type: Boolean, default: false },
  // Find a sale (SoT 9b) quiets the loud type chip — the tile + status carry
  // the type story, and the ★ liked chip is the one chip the boards keep.
  // My Sales / SaleSearchSheet leave this on (default).
  showType: { type: Boolean, default: true },
  // The stretched open control's accessible name ("Open Coastal Simulcast").
  // Setting it turns the card into a browse row that opens: the card itself
  // stays an <article>, a transparent button lies over it and emits `open`,
  // and the accessory sits above that button. Ignored while `interactive` is
  // set — that card is already one button and has no room for a second.
  openLabel: { type: String, default: null },
  // The tile glyph, a PrimeIcons name without `pi-` ('building'). Null draws the
  // sale's own: a bolt while live, a calendar otherwise.
  icon: { type: String, default: null },
});

defineEmits(['select', 'open']);

const slots = useSlots();

const openable = computed(() => (
  !props.interactive && typeof props.openLabel === 'string' && props.openLabel.trim() !== ''
));

const glyphClass = computed(() => {
  if (typeof props.icon === 'string' && props.icon.trim()) return `pi-${props.icon.trim()}`;
  return props.sale.live ? 'pi-bolt' : 'pi-calendar';
});

const showsTypeChip = computed(() => props.showType && typeof props.sale.typeLabel === 'string');

if (import.meta.env?.DEV && props.showType && props.sale && props.sale.typeLabel == null) {
  console.warn('[SaleCard] `sale.typeLabel` is missing while `showType` is on; the type chip is skipped.');
}

// `Object.hasOwn` rather than a lookup with a `??` behind it: `type` is free
// text off a payload, so a sale typed `constructor` or `toString` would
// otherwise resolve to something on Object's prototype instead of to a
// modifier.
const typeModifier = computed(() => (
  Object.hasOwn(TYPE_MODIFIERS, props.sale.type)
    ? TYPE_MODIFIERS[props.sale.type]
    : UNRECOGNIZED_TYPE_MODIFIER
));

const likedLabel = computed(() => {
  if (typeof props.sale.likedLabel === 'string' && props.sale.likedLabel.trim()) {
    return props.sale.likedLabel.trim();
  }
  const count = props.sale.likedCount;
  if (!Number.isInteger(count) || count <= 0) return null;
  return `${count} liked`;
});

const showChipsRow = computed(() => (
  showsTypeChip.value
  || Boolean(likedLabel.value)
  || Boolean(slots.chips)
));
</script>

<style scoped>
.sale-card {
  display: block;
  width: 100%;
  text-align: left;
  background: var(--p-sale-card-background);
  border: var(--p-sale-card-border-width) solid var(--p-sale-card-border-color);
  border-radius: var(--p-sale-card-border-radius);
  padding: var(--p-sale-card-padding);
  font-family: inherit;
  color: var(--p-sale-card-color);
}

button.sale-card {
  cursor: pointer;
}

/* The openable browse row: the card is the positioning box, the open button
   covers it in the card's own corners (so its focus ring draws round the card),
   and the accessory stands above the button. */
.sale-card--openable {
  position: relative;
}

.sale-card__open {
  position: absolute;
  inset: 0;
  /* Out over the card's border as well. An absolutely positioned child is laid
     out against its container's PADDING box, so `inset: 0` alone stopped inside
     the border and left a hairline ring of card that pressed nothing and that
     the focus ring drew inside of — the opposite of what the block comment
     above says this does. The negative margin is what grows the box back out
     over the border, and it replaces `width: 100%`: both edges are placed by
     `inset`, and a width as well would over-constrain them. */
  margin: calc(-1 * var(--p-sale-card-border-width));
  z-index: 1;
  padding: 0;
  border: none;
  border-radius: inherit;
  background: transparent;
  cursor: pointer;
}

.sale-card__open:focus-visible {
  outline: var(--p-sale-card-open-focus-ring-width) solid var(--p-sale-card-open-focus-ring-color);
  outline-offset: 2px;
}

/* Above the open button, but only its controls take a press: a decorative
   accessory (Find a sale's ✓ / › mark) lets the tap fall through and open the
   sale, the way the rest of the card does. */
.sale-card__accessory {
  position: relative;
  z-index: 2;
  flex: none;
  display: flex;
  pointer-events: none;
}

.sale-card__accessory :deep(:is(button, a, input, select, [role='button'], [tabindex])) {
  pointer-events: auto;
}

.sale-card--live {
  border-color: var(--p-sale-card-live-border-color);
}

/* AFTER --live, and that ordering is the promise: the two set the same property
   at the same specificity, so the attended sale keeps its frame while it is
   also the running one. SaleCard.test.js asserts the resolved border rather
   than the classes, because a class assertion would pass either order. */
.sale-card--selected {
  border-color: var(--p-sale-card-selected-border-color);
  background: var(--p-sale-card-selected-background);
}

.sale-card__head {
  display: flex;
  align-items: center;
  gap: var(--p-sale-card-head-gap);
}

.sale-card__tile {
  width: 44px;
  height: 44px;
  border-radius: var(--p-sale-card-tile-border-radius);
  flex: none;
  display: grid;
  place-items: center;
  background: var(--p-sale-card-tile-background);
  color: var(--p-sale-card-tile-color);
}

.sale-card__tile--live {
  background: var(--p-sale-card-tile-live-background);
  color: var(--p-sale-card-tile-live-color);
}

.sale-card__tile i {
  font-size: var(--p-sale-card-tile-icon-font-size);
  line-height: var(--p-sale-card-tile-icon-line-height);
}

.sale-card__info {
  flex: 1;
  min-width: 0;
}

.sale-card__name {
  font-size: var(--p-sale-card-name-font-size);
  font-weight: var(--p-sale-card-name-font-weight);
  letter-spacing: var(--p-sale-card-name-letter-spacing);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--p-sale-card-name-color);
}

.sale-card__subtitle {
  margin-top: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--p-sale-card-status-color);
  font-size: 12px;
}

.sale-card__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--p-sale-card-chips-gap);
  margin-top: 6px;
}

.sale-card__chips > :deep(*) {
  white-space: nowrap;
}

.sale-card__type {
  font-size: var(--p-sale-card-type-font-size);
  font-weight: var(--p-sale-card-type-font-weight);
  letter-spacing: var(--p-sale-card-type-letter-spacing);
  padding: var(--p-sale-card-type-padding);
  border-radius: var(--p-sale-card-type-border-radius);
  white-space: nowrap;
}

/* Each chip carries its OWN hue — not --p-highlight-background, which is the
   mint primary and would give all three the same green wash. Semantic status
   tokens rather than the primitive ramps, so each adapts per scheme, and a
   solid fill under the matching contrast ink rather than a tint of the hue
   under ink of that hue: the tint could not clear 4.5:1 in either scheme, and
   the theme had already made the same call for its status tags. */
.sale-card__type--simulcast {
  background: var(--p-sale-card-type-simulcast-background);
  color: var(--p-sale-card-type-simulcast-color);
}

.sale-card__type--automated {
  background: var(--p-sale-card-type-automated-background);
  color: var(--p-sale-card-type-automated-color);
}

.sale-card__type--timed {
  background: var(--p-sale-card-type-timed-background);
  color: var(--p-sale-card-type-timed-color);
}

/* The one chip that is not a hue: a type the map does not name gets the neutral
   treatment, so nothing about it reads as a fourth auction type. */
.sale-card__type--unrecognized {
  background: var(--p-sale-card-type-unrecognized-background);
  color: var(--p-sale-card-type-unrecognized-color);
}

.sale-card__liked {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--p-sale-card-liked-font-size);
  font-weight: var(--p-sale-card-liked-font-weight);
  letter-spacing: var(--p-sale-card-liked-letter-spacing);
  padding: var(--p-sale-card-liked-padding);
  border-radius: var(--p-sale-card-liked-border-radius);
  background: var(--p-sale-card-liked-background);
  color: var(--p-sale-card-liked-color);
  white-space: nowrap;
}

.sale-card__liked i {
  font-size: var(--p-sale-card-liked-icon-font-size);
}

.sale-card__status {
  font-size: var(--p-sale-card-status-font-size);
  color: var(--p-sale-card-status-color);
  margin-top: 11px;
}

.sale-card__status--live {
  color: var(--p-sale-card-status-live-color);
}

.sale-card__where {
  font-size: 12px;
  color: var(--p-sale-card-status-color);
  margin-top: 4px;
}
</style>
